import React, { useState } from 'react';
import { ChevronDown, Download, ExternalLink } from 'lucide-react';
import { PageLayout } from '../components/PageLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import brandsData from '../data/brands.json';

const publicUrl = process.env.PUBLIC_URL || '';

const brands = brandsData.map((brand) => ({
  ...brand,
  image: `${publicUrl}/brands/${brand.image}`
}));

const slugify = (name) => name.replace(/\s+/g, '-').toLowerCase();

const BrandCard = ({ brand }) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `catalog-panel-${slugify(brand.name)}`;
  const triggerId = `catalog-trigger-${slugify(brand.name)}`;
  const catalogCount = brand.subcategories.length;

  return (
    <Card
      className={`self-start bg-white border-0 transition-all duration-300 ${
        isOpen ? 'shadow-md' : 'shadow-sm hover:shadow-md'
      }`}
    >
      <CardContent className="p-4">
        <button
          type="button"
          id={triggerId}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="w-full text-left group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#262d37] focus-visible:ring-offset-2 rounded-lg"
        >
          <div className="h-32 flex items-center justify-center overflow-hidden rounded-lg mb-3 bg-white px-4">
            <img
              src={brand.image}
              alt={brand.name}
              className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-bold text-[#262d37] leading-snug truncate">
                {brand.name}
              </h2>
              <p className="text-xs text-neutral-500 mt-0.5">
                {catalogCount} {catalogCount === 1 ? 'catálogo' : 'catálogos'}
              </p>
            </div>
            <span
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                isOpen ? 'bg-[#262d37] text-white' : 'bg-[#f2f1ec] text-[#262d37] group-hover:bg-[#262d37] group-hover:text-white'
              }`}
            >
              <ChevronDown
                size={18}
                className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
              />
            </span>
          </div>
        </button>

        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          className={`grid transition-all duration-300 ease-in-out ${
            isOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="space-y-3">
              <a
                href={brand.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full h-9 border-[#262d37] text-[#262d37] hover:bg-[#262d37] hover:text-white"
                >
                  <ExternalLink size={15} />
                  Ver Site
                </Button>
              </a>

              {brand.subcategories.map((sub) => {
                const pdfAvailable = sub.pdfUrl && sub.pdfUrl !== '#';

                return (
                  <div
                    key={sub.name}
                    className="p-3 rounded-lg bg-[#f2f1ec]"
                  >
                    <p className="text-sm font-semibold text-[#262d37] mb-3 leading-snug">
                      {sub.name}
                    </p>

                    {pdfAvailable ? (
                      <a href={sub.pdfUrl} download>
                        <Button
                          type="button"
                          size="sm"
                          className="w-full h-9 bg-[#262d37] text-white hover:bg-neutral-800"
                        >
                          <Download size={15} />
                          Descarregar PDF
                        </Button>
                      </a>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        disabled
                        className="w-full h-9 bg-neutral-100 text-neutral-400 cursor-not-allowed"
                        title="PDF disponível em breve"
                      >
                        <Download size={15} />
                        PDF em breve
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const Catalogs = () => {
  return (
    <PageLayout>
      <section className="relative -mt-20 lg:pt-40 pt-28 pb-16 md:pb-20 bg-[#262d37] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">Catálogos</h1>
            <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
              Consulte os catálogos das nossas marcas parceiras de iluminação exterior e interior.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20 bg-[#e8e6df]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            {brands.map((brand) => (
              <BrandCard key={brand.name} brand={brand} />
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
